import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DlqRow {
  readonly dlqId: string;
  readonly dlqCode: string;
  readonly queueName: string;
  readonly messageType: string;
  readonly failureReason: string;
  readonly retryAttempts: number;
  readonly replayCount: number;
  readonly status: string;
}

const STATUSES = ['', 'PENDING', 'REPLAYED', 'DISCARDED', 'RESOLVED'];

/**
 * GR L29.05 — Dead-letter Queue (`/admin/esb/dlq`). Triage surface for the GR
 * net-new `esb_dlq` ledger: poison messages that failed delivery, with their
 * failure reason and retry history. Operators can replay a pending entry back
 * onto its queue. Filterable by status.
 */
export function GrEsbDlqPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<DlqRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = status ? `?status=${status}` : '';
    apiRequest<{ data: DlqRow[] }>(`/v1/eip/dlq${qs}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, refresh]);

  async function replay(id: string): Promise<void> {
    try {
      await apiRequest(`/v1/eip/dlq/${id}/replay`, { token, method: 'PUT', body: {} });
      setNotice(t('gr29.dlq.notice'));
      setError(null);
      setRefresh((n) => n + 1);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <main aria-label="esb-dlq">
      <h1>{t('gr29.dlq.title')}</h1>
      <div className="filters">
        <label>
          {t('gr29.dlq.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr29.dlq.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {notice && <p aria-label="notice" role="status">{notice}</p>}
      {loading ? <p>{t('gr29.dlq.loading')}</p> : (
        <>
          <p aria-label="dlq-count">{t('gr29.dlq.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr29.dlq.empty')}</p> : (
            <table aria-label="dlq-table">
              <thead><tr><th>{t('gr29.dlq.colCode')}</th><th>{t('gr29.dlq.colQueue')}</th><th>{t('gr29.dlq.colType')}</th><th>{t('gr29.dlq.colReason')}</th><th>{t('gr29.dlq.colRetries')}</th><th>{t('gr29.dlq.colStatus')}</th><th></th></tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.dlqId}>
                    <td>{d.dlqCode}</td><td>{d.queueName}</td><td>{d.messageType}</td>
                    <td>{d.failureReason}</td><td>{d.retryAttempts}</td><td>{d.status}</td>
                    <td>
                      {d.status === 'PENDING' && (
                        <button type="button" aria-label={`replay-${d.dlqId}`} onClick={() => replay(d.dlqId)}>{t('gr29.dlq.replay')}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
